import useWfWorkplaceService, {
  WorkplaceReservationCancelInitialParams,
} from 'src/api/wf/service/WfWorkplaceService';
import { useUserAgent } from 'src/common/userAgent/useUserAgent';
import Button from '../button/Button';
import { wfNavigate } from '../../router/WfScreenRegistration';
import { useNavigate } from 'react-router-dom';
import { WfScreensMap } from '../../router/WfScreensMap';
import { WF_Screen } from '../../router/WfScreens';
import Input from '../input/Input';
import { useTranslation } from 'react-i18next';
import { useWfUserStore } from 'src/api/wf/stores/WfUserStore';
import { MagnifyingGlassCircleIcon } from '@heroicons/react/24/solid';
import WF_MapSearchHeader from '../mapSearchHeader/WF_MapSearchHeader';
import InlineAlert from 'src/ui/wf/common/inlineAlert/InlineAlert';
import { useWfWorkplaceStore } from 'src/api/wf/stores/WfWorkplaceStore';
import { useNotify } from 'src/ui/common/alert/useNotify';
import { useWfFilterStore } from 'src/api/wf/stores/WfFilterStore';
import { useWfLocationService } from 'src/api/wf/service/WfLocationService';
import { FormRow } from '../form/FormRow';
import clsx from 'clsx';

type Props = {
  onSearch: () => void;
};

function WF_ListSearchHeader({ onSearch }: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const userAgent = useUserAgent();
  const { selectedUser, user, setSelectedUser } = useWfUserStore();
  const { selectedWorkplace, setSelectedWorkplace } = useWfWorkplaceStore();
  const { filterParameters } = useWfFilterStore();
  const { prepareWfWorkplaceCSVDownload, createWorkplaceReservation, cancelWorkplaceReservation } =
    useWfWorkplaceService();
  const { updateWfUserLocation } = useWfLocationService();
  const notify = useNotify();

  const handleRegistration = () => {
    wfNavigate(navigate, WfScreensMap[WF_Screen.WorkplaceDetails].path);
  };

  const handleAlertHistory = () => {
    wfNavigate(navigate, WfScreensMap[WF_Screen.AlertHistory].path);
  };

  const handleCSVDownload = async () => {
    try {
      const result = await prepareWfWorkplaceCSVDownload();
      if (!result) return;

      const { blob, filename } = result;

      if (!blob || blob.size <= 4) {
        console.warn('No data available for download.');
        return;
      }

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download failed:', err);
    }
  };

  const handleReservation = async () => {
    const engineer = userAgent.isDesktop ? selectedUser : user;

    if (!selectedWorkplace) {
      notify('wf.searchHeader.noWorkplaceSelected');
      return;
    }

    if (!engineer) {
      notify('wf.searchHeader.noWorkerSelected');
      return;
    }

    const reservationParams = {
      engineerUserId: engineer.id,
      customerId: selectedWorkplace.customerId,
      branchCode: selectedWorkplace.branchCode,
    };

    try {
      if (selectedWorkplace.details.reservations.length > 0) {
        notify('wf.searchHeader.reservationExists');
        return;
      }

      const response = await createWorkplaceReservation(reservationParams);

      if (response) {
        notify('wf.searchHeader.reservationSuccess');
        if (userAgent.isMobile) {
          updateWfUserLocation();
        }
      } else {
        notify('wf.searchHeader.reservationFailed');
      }
    } catch (error) {
      console.error('Error when trying to reserve with the data:', error);
      notify('wf.searchHeader.reservationFailed');
    } finally {
      setSelectedWorkplace(null);
      setSelectedUser(null);
    }
  };

  const handleCancelReservation = async (reservation: WorkplaceReservationCancelInitialParams) => {
    try {
      const response = await cancelWorkplaceReservation(reservation);

      if (response) {
        notify('wf.searchHeader.cancelReservationSuccess');
        if (userAgent.isMobile) {
          updateWfUserLocation();
        }
      } else {
        notify('wf.searchHeader.cancelReservationFailed');
      }
    } catch (error) {
      console.error('Error with canceling or fetching updated data:', error);
      notify('wf.searchHeader.cancelReservationFailed');
    }
  };

  const handleCancelSelectedReservations = async () => {
    if (!selectedWorkplace) {
      notify('wf.searchHeader.noWorkplaceSelected');
      return;
    }

    if (selectedWorkplace.details.reservations.length === 0) {
      notify('wf.searchHeader.noReservationsToCancel');
      return;
    }

    const reservationsToCancel = selectedWorkplace.details.reservations;

    reservationsToCancel.forEach((reservationToCancel) => {
      const reservationParams = {
        engineerUserId: reservationToCancel.engineerUserId,
        reserverUserId: reservationToCancel.reserverUserId,
        customerId: selectedWorkplace.customerId,
        branchCode: selectedWorkplace.branchCode,
      };

      handleCancelReservation(reservationParams);
    });

    setSelectedWorkplace(null);
    setSelectedUser(null);
  };

  const mobileActionButtonClassName = userAgent.isMobile ? 'w-full' : '';

  return (
    <div>
      <InlineAlert />
      <WF_MapSearchHeader onSearch={onSearch} showNoResults={false} />

      <div
        className={clsx(
          userAgent.isDesktop
            ? 'my-2 flex flex-wrap items-center gap-x-4 gap-y-2'
            : 'grid grid-cols-2 gap-2 p-2 px-4'
        )}
      >
        {userAgent.isDesktop && (
          <>
            <Button
              title={t('wf.csv.csvDownload')}
              onClick={handleCSVDownload}
              disabled={!filterParameters.branch}
            />
            <FormRow title={t('wf.searchHeader.maintenanceStaff')}>
              <div className="flex items-center">
                <Input className="h-8 rounded-r-none" value={selectedUser?.name || ''} disabled />
                <Button
                  data-testid="search-icon"
                  iconOnly
                  startIcon={<MagnifyingGlassCircleIcon className="size-5" />}
                  onClick={() => {
                    wfNavigate(navigate, WfScreensMap[WF_Screen.UserSearch].path);
                  }}
                />
              </div>
            </FormRow>
          </>
        )}

        <Button
          title={
            userAgent.isDesktop
              ? t('wf.searchHeader.proxyReservation')
              : t('wf.searchHeader.reservation')
          }
          onClick={handleReservation}
          className={mobileActionButtonClassName}
        />
        <Button
          title={t('wf.propertyList.cancelReservation')}
          onClick={handleCancelSelectedReservations}
          className={mobileActionButtonClassName}
        />
        <Button
          title={t('wf.common.register')}
          onClick={handleRegistration}
          disabled={!selectedWorkplace}
          className={mobileActionButtonClassName}
        />
        <Button
          title={t('wf.searchHeader.alertHistory')}
          onClick={handleAlertHistory}
          disabled={!selectedWorkplace}
          className={mobileActionButtonClassName}
        />
      </div>
    </div>
  );
}

export default WF_ListSearchHeader;
